export type AfishaApiEvent = {
  id: number;
  title: string;
  body: string;
  post_id: number;
  source_id: string;
  event_time: string;
  list_of_dates?: string;
  /** Расстояние до категории (отдаётся только в dev-режиме; MySQL может вернуть строкой). */
  tag_diff?: number | string | null;
};

export type AfishaEvent = {
  id: number;
  time: string;
  title: string;
  body: string;
  tgUrl: string;
  /** Расстояние до категории (только в dev-режиме). */
  tag_diff?: number;
};

export type AfishaDay = {
  date: string;
  weekday: string;
  dayNum: string;
  monthLabel: string;
  events: AfishaEvent[];
};

const WEEKDAY_SHORT = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
const MONTH_SHORT = [
  "янв",
  "фев",
  "мар",
  "апр",
  "мая",
  "июн",
  "июл",
  "авг",
  "сен",
  "окт",
  "ноя",
  "дек",
];

export function getTodayDateStr(): string {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Ссылка на tg-пост по source_id и post_id (как в остальном приложении). */
export function buildTgUrl(sourceId: string, postId: number): string {
  const channel = (sourceId || "").split("_").slice(0, -1).join("_");
  if (!channel || !postId) return "";
  return `https://t.me/${channel}/${postId}`;
}

/** 8 дней: сегодня … сегодня+7 (как диапазон на бэкенде). */
export function buildDaysRange(daysCount = 8): Omit<AfishaDay, "events">[] {
  const days: Omit<AfishaDay, "events">[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < daysCount; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");

    days.push({
      date: `${yyyy}-${mm}-${dd}`,
      weekday: WEEKDAY_SHORT[date.getDay()],
      dayNum: String(date.getDate()),
      monthLabel: MONTH_SHORT[date.getMonth()],
    });
  }

  return days;
}

function eventTitle(row: AfishaApiEvent): string {
  const title = row.title?.trim();
  if (title) return title;
  const fromBody = row.body?.trim().slice(0, 120);
  return fromBody || "Без названия";
}

function eventTime(row: AfishaApiEvent): string {
  return row.event_time?.trim() || "—";
}

function eventTagDiff(row: AfishaApiEvent): number | undefined {
  // MySQL возвращает DECIMAL как строку — приводим к числу
  const num = typeof row.tag_diff === "string" ? Number(row.tag_diff) : row.tag_diff;
  return typeof num === "number" && Number.isFinite(num) ? num : undefined;
}

/** Парсит "19:00" / "9.30" в минуты от полуночи; null — если время не указано. */
export function parseTimeToMinutes(time: string): number | null {
  if (!time || time === "—") return null;

  const match = time.trim().match(/^(\d{1,2})[:.](\d{2})$/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  return hours * 60 + minutes;
}

/** Сортировка по времени: от ранних к поздним, без времени — в конце. */
export function compareEventTime(a: string, b: string): number {
  const minutesA = parseTimeToMinutes(a);
  const minutesB = parseTimeToMinutes(b);

  if (minutesA === null && minutesB === null) return 0;
  if (minutesA === null) return 1;
  if (minutesB === null) return -1;

  return minutesA - minutesB;
}

export function sortEventsByTime(events: AfishaEvent[]): AfishaEvent[] {
  return [...events].sort((a, b) => compareEventTime(a.time, b.time));
}

export function normalizeDateStr(value: unknown): string {
  if (value == null || value === "") return "";

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const yyyy = value.getFullYear();
    const mm = String(value.getMonth() + 1).padStart(2, "0");
    const dd = String(value.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  const str = String(value).trim();
  const match = str.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : str.slice(0, 10);
}

export function buildDayMeta(dateInput: string | Date): Omit<AfishaDay, "events"> {
  const dateStr = normalizeDateStr(dateInput);
  const [yyyy, mm, dd] = dateStr.split("-").map(Number);
  const date = new Date(yyyy, mm - 1, dd);

  return {
    date: dateStr,
    weekday: WEEKDAY_SHORT[date.getDay()],
    dayNum: String(date.getDate()),
    monthLabel: MONTH_SHORT[date.getMonth()],
  };
}

export function mapApiEventsToDayEvents(
  apiEvents: AfishaApiEvent[],
): AfishaEvent[] {
  return sortEventsByTime(
    apiEvents.map((row) => ({
      id: row.id,
      time: eventTime(row),
      title: eventTitle(row),
      body: row.body?.trim() || "",
      tgUrl: buildTgUrl(row.source_id, row.post_id),
      tag_diff: eventTagDiff(row),
    })),
  );
}

export function parseEventDates(raw: string): string[] {
  return (raw || "")
    .split(",")
    .map((d) => normalizeDateStr(d.trim()))
    .filter(Boolean);
}

/** Группирует события API по датам (для фильтра и поиска). */
export function mapApiEventsToEventsByDate(
  apiEvents: AfishaApiEvent[],
  allowedDates: string[],
): Record<string, AfishaEvent[]> {
  const allowed = new Set(allowedDates);
  const byDate: Record<string, AfishaEvent[]> = {};

  for (const date of allowedDates) {
    byDate[date] = [];
  }

  for (const row of apiEvents) {
    const event: AfishaEvent = {
      id: row.id,
      time: eventTime(row),
      title: eventTitle(row),
      body: row.body?.trim() || "",
      tgUrl: buildTgUrl(row.source_id, row.post_id),
      tag_diff: eventTagDiff(row),
    };

    for (const date of parseEventDates(row.list_of_dates || "")) {
      if (!allowed.has(date)) continue;
      if (byDate[date].some((item) => item.id === event.id)) continue;
      byDate[date].push(event);
    }
  }

  for (const date of allowedDates) {
    byDate[date] = sortEventsByTime(byDate[date]);
  }

  return byDate;
}

/** Группирует события API по дням для полоски дат и списка треков. */
export function mapApiEventsToDays(apiEvents: AfishaApiEvent[]): AfishaDay[] {
  const dayTemplates = buildDaysRange(8);
  const eventsByDate = new Map<string, AfishaEvent[]>();

  for (const day of dayTemplates) {
    eventsByDate.set(day.date, []);
  }

  for (const row of apiEvents) {
    const dates = parseEventDates(row.list_of_dates || "");
    const event: AfishaEvent = {
      id: row.id,
      time: eventTime(row),
      title: eventTitle(row),
      body: row.body?.trim() || "",
      tgUrl: buildTgUrl(row.source_id, row.post_id),
      tag_diff: eventTagDiff(row),
    };

    for (const date of dates) {
      if (!eventsByDate.has(date)) continue;
      eventsByDate.get(date)!.push(event);
    }
  }

  eventsByDate.forEach((events, date) => {
    eventsByDate.set(date, sortEventsByTime(events));
  });

  return dayTemplates.map((day) => ({
    ...day,
    events: eventsByDate.get(day.date) ?? [],
  }));
}
